import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MenuItem, MenuStyle } from '@busbook/shared';
import { useFeature, useTenant } from '../../lib/tenant.js';
import { useT } from '../../i18n/index.js';
import { AccountButton } from './AccountButton.js';
import { LocalePicker } from './LocalePicker.js';
import { Glyph } from './Glyph.js';
import { activeIndex, ROUTE_FOR, visibleItems } from './items.js';

/**
 * The app frame: brand, navigation, account.
 *
 * Five shapes, one idea. Navigation is a ROUTE — a line of stops with a marker on the one
 * you are at — because that is what this app is about, and because it gives the passenger
 * the same answer to "where am I" whichever shape their operator was sold. What changes
 * between shapes is structural: where the line runs, and how much of the screen it costs.
 *
 * Which shape a tenant gets belongs to the platform, not the operator (DECISIONS D69).
 */

interface NavProps {
  items: MenuItem[];
  active: number;
}

/** Position of the marker on the line, as custom properties the stylesheet reads. */
function stopVars(items: MenuItem[], active: number): CSSProperties {
  return {
    ['--stops' as string]: String(Math.max(items.length, 1)),
    ['--stop' as string]: String(Math.max(active, 0)),
    ['--stop-shown' as string]: active < 0 ? '0' : '1',
  };
}

/* ---------------------------------------------------------------- wordmark */

export function Wordmark({ className }: { className?: string }) {
  const { branding } = useTenant();
  return (
    <Link to="/" className={className ? `nav-wordmark ${className}` : 'nav-wordmark'}>
      {branding.logoUrl ? (
        <img src={branding.logoUrl} alt="" className="nav-wordmark-logo" />
      ) : (
        <span className="nav-wordmark-initial" aria-hidden>
          {branding.appName.slice(0, 1).toUpperCase()}
        </span>
      )}
      <span className="nav-wordmark-name">{branding.appName}</span>
    </Link>
  );
}

/* ------------------------------------------------------------------ header */

/**
 * Always present, whatever the navigation shape.
 *
 * The bottom-tab layouts used to render no header at all, which cost the operator their own
 * name at the top of their own app and left the passenger no way to sign out except by
 * finding the bottom of the Help page.
 */
function AppHeader({
  style,
  items,
  active,
  onOpenMenu,
}: NavProps & { style: MenuStyle; onOpenMenu?: () => void }) {
  const t = useT();
  const wide = style === 'TOP_BAR';
  return (
    <header className="nav-header nav-frost">
      <div className="nav-header-inner" style={{ maxWidth: wide ? '60rem' : '40rem' }}>
        {onOpenMenu && (
          <button type="button" onClick={onOpenMenu} aria-label={t('n.openMenu')} className="nav-icon-button">
            <Glyph name="menu" size={22} />
          </button>
        )}
        <Wordmark />
        {style === 'TOP_BAR' ? <TopChips items={items} active={active} /> : <span className="flex-1" />}
        {/* Language then account, in that order: one is a setting, the other is who you are,
            and the thing that identifies the passenger sits at the end of the bar. */}
        <LocalePicker />
        <AccountButton />
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------- top bar */

function TopChips({ items, active }: NavProps) {
  return (
    <nav aria-label="Main" className="nav-chips">
      {items.map((item, index) => (
        <Link
          key={item.key}
          to={ROUTE_FOR[item.key]!}
          className="nav-chip"
          data-active={index === active}
          aria-current={index === active ? 'page' : undefined}
        >
          {item.label}
          {index === active && <span className="nav-stop" aria-hidden />}
        </Link>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------- bottom tabs */

function Tab({ item, isActive }: { item: MenuItem; isActive: boolean }) {
  return (
    <Link
      to={ROUTE_FOR[item.key]!}
      className="nav-tab"
      data-active={isActive}
      aria-current={isActive ? 'page' : undefined}
    >
      <Glyph name={item.key} active={isActive} />
      <span className="nav-tab-label">{item.label}</span>
    </Link>
  );
}

function BottomTabs({ items, active }: NavProps) {
  return (
    <div className="nav-dock-wrap nav-frost">
      <nav aria-label="Main" className="nav-tabs" style={stopVars(items, active)}>
        <span className="nav-route" style={{ top: -1 }} aria-hidden>
          <span className="nav-route-mark" />
        </span>
        {items.map((item, index) => (
          <Tab key={item.key} item={item} isActive={index === active} />
        ))}
      </nav>
    </div>
  );
}

/* ----------------------------------------------------------- floating dock */

function FloatingDock({ items, active }: NavProps) {
  return (
    <div className="nav-island-wrap">
      <nav aria-label="Main" className="nav-island nav-frost" style={stopVars(items, active)}>
        <span className="nav-route" aria-hidden>
          <span className="nav-route-mark" />
        </span>
        {items.map((item, index) => (
          <Tab key={item.key} item={item} isActive={index === active} />
        ))}
      </nav>
    </div>
  );
}

/* ------------------------------------------------------------------- rail */

function Rail({ items, active }: NavProps) {
  return (
    <div className="nav-rail">
      <Link to="/" aria-label="Home" className="nav-wordmark">
        <RailMark />
      </Link>
      <nav aria-label="Main" className="nav-rail-list">
        {/* The line spans the stops, not the rail: a route that carries on past the last
            destination and down to the account is a line about nothing. */}
        <div className="nav-rail-stops" style={stopVars(items, active)}>
          <span className="nav-route-v" aria-hidden>
            <span className="nav-route-mark" />
          </span>
          {items.map((item, index) => (
            <Link
              key={item.key}
              to={ROUTE_FOR[item.key]!}
              className="nav-rail-item"
              data-active={index === active}
              aria-current={index === active ? 'page' : undefined}
              title={item.label}
            >
              <Glyph name={item.key} active={index === active} size={21} />
              <span className="nav-rail-label">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
      <div className="nav-rail-foot">
        <LocalePicker variant="icon" />
        <AccountButton variant="icon" />
      </div>
    </div>
  );
}

function RailMark() {
  const { branding } = useTenant();
  // The square icon first: a wide wordmark does not fit a rail, and the icon is the mark
  // an operator uploaded precisely for places this narrow.
  const mark = branding.iconUrl ?? branding.logoUrl;
  if (!mark) {
    return (
      <span className="nav-wordmark-initial" aria-hidden>
        {branding.appName.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return <img src={mark} alt="" className="nav-wordmark-logo" />;
}

/* ----------------------------------------------------------------- drawer */

function Drawer({ items, active, onClose }: NavProps & { onClose: () => void }) {
  const t = useT();

  // A drawer that survives navigation is a drawer the passenger has to close twice.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-40">
      <button type="button" aria-label={t('n.closeMenu')} onClick={onClose} className="nav-scrim" />
      <div className="nav-drawer">
        <div className="nav-drawer-head">
          <Wordmark />
          <button type="button" onClick={onClose} aria-label={t('n.closeMenu')} className="nav-icon-button">
            <Glyph name="close" size={20} />
          </button>
        </div>
        <nav aria-label="Main" className="nav-drawer-list">
          <div className="nav-drawer-stops">
          {items.map((item, index) => (
            <Link
              key={item.key}
              to={ROUTE_FOR[item.key]!}
              onClick={onClose}
              className="nav-drawer-item"
              data-active={index === active}
              aria-current={index === active ? 'page' : undefined}
            >
              {index === active && <span className="nav-stop" aria-hidden />}
              <Glyph name={item.key} active={index === active} size={20} />
              {item.label}
            </Link>
          ))}
          </div>
        </nav>
        <div className="border-t p-2" style={{ borderColor: 'var(--color-line)' }}>
          <AccountButton variant="block" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ shell */

export function AppShell({ style, items, children }: { style: MenuStyle; items: MenuItem[]; children: ReactNode }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Read unconditionally rather than inside the filter: hooks cannot be called per item,
  // and the list of feature-gated entries is short enough to name each one.
  const travellers = useFeature('travellers');
  const shown = visibleItems(items, (key) => (key === 'travellers' ? travellers : true));
  const active = activeIndex(shown, location.pathname);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const empty = shown.length === 0;
  const drawer = style === 'SIDE_DRAWER' && !empty;

  return (
    <div className="app-shell" data-nav={empty ? 'NONE' : style}>
      <AppHeader
        style={style}
        items={shown}
        active={active}
        onOpenMenu={drawer ? () => setDrawerOpen(true) : undefined}
      />

      <main className="app-main" style={{ maxWidth: style === 'TOP_BAR' ? '60rem' : '40rem' }}>
        {children}
      </main>

      {!empty && style === 'BOTTOM_TABS' && <BottomTabs items={shown} active={active} />}
      {!empty && style === 'FLOATING_DOCK' && <FloatingDock items={shown} active={active} />}
      {/* Both are in the tree; the stylesheet decides which width gets which, alongside the
          insets and the header that the same breakpoint turns off. One breakpoint, one place. */}
      {!empty && style === 'RAIL' && (
        <>
          <Rail items={shown} active={active} />
          <FloatingDock items={shown} active={active} />
        </>
      )}
      {drawerOpen && drawer && (
        <Drawer items={shown} active={active} onClose={() => setDrawerOpen(false)} />
      )}
    </div>
  );
}
